<template>
  <div class="container-fluid py-3" @click="detailPopup = null">
    <!-- Header toolbar -->
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <div class="d-flex align-items-center gap-2">
        <i class="material-icons-round text-purple" style="font-size:28px;">calendar_month</i>
        <h5 class="mb-0">{{ $t('golf.planner') }}</h5>
      </div>

      <!-- Axis toggle (Users vs Equipment) — Equipment only shown when future reservation config is on -->
      <div v-if="enableFutureReservation && !plannerConfigs.disableTools" class="btn-group" role="group">
        <button
          v-for="a in axes"
          :key="a.key"
          class="btn btn-sm"
          :class="axisView === a.key ? 'btn-purple' : 'btn-outline-secondary'"
          @click="axisView = a.key"
        >
          <i class="material-icons-round me-1" style="font-size:14px;vertical-align:middle;">{{ a.icon }}</i>{{ $t(a.label) }}
        </button>
      </div>

      <!-- View toggle -->
      <div class="btn-group" role="group">
        <button
          v-for="v in views"
          :key="v.key"
          class="btn btn-sm"
          :class="currentView === v.key ? 'btn-purple' : 'btn-outline-secondary'"
          @click="currentView = v.key"
        >{{ $t(v.label) }}</button>
      </div>

      <!-- Date navigation -->
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-sm btn-outline-secondary" @click="navigate(-1)">
          <i class="material-icons-round" style="font-size:18px;">chevron_left</i>
        </button>
        <span class="fw-bold" style="min-width:180px;text-align:center;">{{ headingLabel }}</span>
        <button class="btn btn-sm btn-outline-secondary" @click="navigate(1)">
          <i class="material-icons-round" style="font-size:18px;">chevron_right</i>
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="goToday">{{ $t('golf.today') }}</button>
      </div>

      <div v-if="isAdmin" class="d-flex gap-2">
        <template v-if="axisView === 'user'">
          <button class="btn btn-sm btn-purple" @click="openCreateAssignment(null, null)">
            <i class="material-icons-round me-1" style="font-size:16px;">add</i>
            {{ $t('golf.addAssignment') }}
          </button>
          <button v-if="!plannerConfigs.disableTaskCreation" class="btn btn-sm btn-outline-secondary" @click="openCreate(null, null)">
            <i class="material-icons-round me-1" style="font-size:16px;">add_task</i>
            {{ $t('golf.scheduleTask') }}
          </button>
        </template>
        <template v-else-if="axisView === 'equipment' && enableFutureReservation">
          <button class="btn btn-sm btn-purple" @click="openCreateReservation(null, null)">
            <i class="material-icons-round me-1" style="font-size:16px;">add</i>
            {{ $t('golf.addReservation') }}
          </button>
        </template>
      </div>
    </div>

    <!-- Routines toolbar -->
    <div v-if="isAdmin && axisView === 'user' && !plannerConfigs.disableRoutines" class="routines-bar d-flex align-items-center flex-wrap gap-2 mb-3 p-2 rounded">
      <i class="material-icons-round text-purple" style="font-size:20px;">loop</i>
      <span class="fw-semibold small me-1">{{ $t('golf.routines') }}</span>

      <select v-model="selectedRoutineId" class="form-select form-select-sm" style="max-width:220px;">
        <option :value="null">— {{ $t('golf.selectRoutine') }} —</option>
        <option v-for="r in routines" :key="r.id" :value="r.id">{{ r.name }}</option>
      </select>

      <button
        class="btn btn-sm btn-purple mb-0"
        :disabled="!selectedRoutineId || applyingRoutine"
        @click="applyRoutine"
      >
        <span v-if="applyingRoutine" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="material-icons-round me-1" style="font-size:15px;">play_arrow</i>
        {{ $t('golf.applyRoutine') }}
      </button>

      <button
        v-if="selectedRoutineId"
        class="btn btn-sm btn-outline-danger mb-0"
        @click="deleteRoutine"
      >
        <i class="material-icons-round me-1" style="font-size:16px;vertical-align:middle;">delete</i>
        {{ $t('golf.deleteRoutine') }}
      </button>

      <button class="btn btn-sm btn-outline-secondary ms-auto mb-0" @click="openRoutineBuilder">
        <i class="material-icons-round me-1" style="font-size:15px;">add</i>
        {{ $t('golf.createRoutine') }}
      </button>
    </div>

    <!-- Legend -->
    <div v-if="axisView === 'user' && plannerConfigs.useStatusColors" class="d-flex align-items-center gap-3 mb-2 small text-muted">
      <span><span class="legend-dot legend-status-pending"></span> {{ $t('projectAssignments.notFinished') }}</span>
      <span><span class="legend-dot legend-status-ongoing"></span> {{ $t('projectAssignments.ongoing') }}</span>
      <span><span class="legend-dot legend-status-finished"></span> {{ $t('projectAssignments.finished') }}</span>
      <span v-if="!plannerConfigs.disableTaskCreation"><span class="legend-dot legend-task"></span> {{ $t('golf.tasks') }}</span>
    </div>
    <div v-else-if="axisView === 'user'" class="d-flex align-items-center gap-3 mb-2 small text-muted">
      <span><span class="legend-dot legend-assignment"></span> {{ $t('golf.projectAssignments') }}</span>
      <span v-if="!plannerConfigs.disableTaskCreation"><span class="legend-dot legend-task"></span> {{ $t('golf.tasks') }}</span>
    </div>
    <div v-else class="d-flex align-items-center gap-3 mb-2 small text-muted">
      <span><span class="legend-dot legend-reservation-active"></span> {{ $t('golf.confirmedReservation') }}</span>
      <span><span class="legend-dot legend-reservation-pending"></span> {{ $t('golf.pendingReservation') }}</span>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-purple"></div>
    </div>

    <!-- DAY VIEW — Gantt -->
    <div v-else-if="currentView === 'day'" class="gantt-wrapper">
      <div ref="ganttEl" class="gantt-container">
        <!-- Time header -->
        <div class="gantt-header">
          <div class="gantt-label-col"></div>
          <div class="gantt-time-area">
            <div
              v-for="h in hours"
              :key="h"
              class="gantt-hour-cell"
              :style="{ width: hourWidth + 'px' }"
            >{{ String(h).padStart(2,'0') }}:00</div>
          </div>
        </div>

        <!-- USER AXIS rows -->
        <template v-if="axisView === 'user'">
          <!-- Unassigned row -->
          <div
            class="gantt-row"
            :class="{ 'drag-over': dragOverUser === null }"
            :style="rowMinHeight(null)"
            @dragover.prevent="dragOverUser = null"
            @dragleave="onDragLeave(null)"
          >
            <div class="gantt-label-col text-muted fst-italic small">{{ $t('golf.unassigned') }}</div>
            <div
              class="gantt-bar-area"
              :style="{ width: totalWidth + 'px' }"
              :title="isAdmin ? $t('golf.clickToAddAssignment') : ''"
              @click.self="openCreateAssignment(null, $event)"
              @drop="onDrop(null, $event)"
            >
              <div
                v-for="a in unassignedDayAssignments"
                :key="'a'+a.id"
                class="gantt-bar gantt-bar-assignment"
                :style="assignmentBarStyle(a)"
                :title="assignmentLabel(a)"
                draggable="true"
                @dragstart="onDragStartAssignment(a, $event)"
                @click.stop="showBarDetail('assignment', a, $event)"
              >
                <span class="gantt-bar-label">{{ assignmentLabel(a) }}</span>
              </div>
              <div
                v-for="task in unassignedDayTasks"
                :key="'t'+task.id"
                class="gantt-bar gantt-bar-task"
                :style="taskBarStyle(task)"
                :title="task.title"
                draggable="true"
                @dragstart="onDragStart(task, $event)"
                @click.stop="openEdit(task)"
              >
                <span class="gantt-bar-label">{{ task.title }}</span>
              </div>
              <div v-for="h in hours" :key="'g'+h" class="gantt-grid-line" :style="{ left: h * hourWidth + 'px' }"></div>
            </div>
          </div>

          <!-- User rows -->
          <div
            v-for="user in visibleUsers"
            :key="user.id"
            class="gantt-row"
            :class="{ 'drag-over': dragOverUser === user.id }"
            :style="rowMinHeight(user.id)"
            @dragover.prevent="dragOverUser = user.id"
            @dragleave="onDragLeave(user.id)"
          >
            <div class="gantt-label-col">
              <img v-if="userImages[user.id]" :src="userImages[user.id]" class="planner-avatar" alt="" />
              <span v-else class="planner-avatar planner-avatar-initials">{{ userInitials(user) }}</span>
              <span class="gantt-user-name">{{ user.name || user.userName }}</span>
            </div>
            <div
              class="gantt-bar-area"
              :style="{ width: totalWidth + 'px' }"
              :title="isAdmin ? $t('golf.clickToAddAssignment') : ''"
              @click.self="openCreateAssignment(user.id, $event)"
              @drop="onDrop(user.id, $event)"
            >
              <div
                v-for="a in assignmentsForUser(user.id)"
                :key="'a'+a.id"
                class="gantt-bar gantt-bar-assignment"
                :style="assignmentBarStyle(a)"
                :title="assignmentLabel(a)"
                draggable="true"
                @dragstart="onDragStartAssignment(a, $event)"
                @click.stop="showBarDetail('assignment', a, $event)"
              >
                <span class="gantt-bar-label">{{ assignmentLabel(a) }}</span>
              </div>
              <div
                v-for="task in tasksForUser(user.id)"
                :key="'t'+task.id"
                class="gantt-bar gantt-bar-task"
                :style="taskBarStyle(task)"
                :title="task.title"
                draggable="true"
                @dragstart="onDragStart(task, $event)"
                @click.stop="openEdit(task)"
              >
                <span class="gantt-bar-label">{{ task.title }}</span>
              </div>
              <div v-for="h in hours" :key="'g'+h" class="gantt-grid-line" :style="{ left: h * hourWidth + 'px' }"></div>
            </div>
          </div>
        </template>

        <!-- EQUIPMENT AXIS rows -->
        <template v-else>
          <div v-if="equipmentRows.length === 0" class="text-center text-muted py-4 small fst-italic">
            {{ $t('golf.noToolReservations') }}
          </div>
          <div
            v-for="tool in equipmentRows"
            :key="'tool'+toolKey(tool.id)"
            class="gantt-row"
          >
            <div class="gantt-label-col">
              <img v-if="toolImages[toolKey(tool.id)]" :src="toolImages[toolKey(tool.id)]" class="planner-avatar" alt="" />
              <span v-else class="planner-avatar planner-avatar-initials"><i class="material-icons-round" style="font-size:14px;">build</i></span>
              <span class="gantt-user-name">{{ tool.name }}</span>
            </div>
            <div
              class="gantt-bar-area"
              :style="{ width: totalWidth + 'px' }"
              :title="(isAdmin && enableFutureReservation) ? $t('golf.clickToAddReservation') : ''"
              @click.self="openCreateReservation(tool, $event)"
            >
              <div
                v-for="a in assignmentsForTool(tool.id)"
                :key="'r'+a.id"
                class="gantt-bar"
                :class="a.active ? 'gantt-bar-reservation-active' : 'gantt-bar-reservation-pending'"
                :style="assignmentBarStyle(a)"
                :title="assignmentLabel(a)"
                @click.stop="showBarDetail('assignment', a, $event)"
              >
                <span class="gantt-bar-label">{{ assignmentLabel(a) }}</span>
              </div>
              <div v-for="h in hours" :key="'g'+h" class="gantt-grid-line" :style="{ left: h * hourWidth + 'px' }"></div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- WEEK VIEW -->
    <div v-else-if="currentView === 'week'" class="card">
      <div class="table-responsive">
        <table class="table table-bordered mb-0 week-table">
          <thead>
            <tr>
              <th class="week-label-col"></th>
              <th
                v-for="d in weekDays"
                :key="d.iso"
                class="text-center"
                :class="{ 'bg-purple-light': isDateToday(d.date) }"
              >
                <div class="fw-bold">{{ d.weekday }}</div>
                <div class="small text-muted">{{ d.label }}</div>
              </th>
            </tr>
          </thead>
          <tbody v-if="axisView === 'user'">
            <tr v-for="user in allRows" :key="user.id ?? 'unassigned'">
              <td class="week-label-col small fw-semibold">
                <img v-if="userImages[user.id]" :src="userImages[user.id]" class="planner-avatar" alt="" />
                <span v-else-if="user.id" class="planner-avatar planner-avatar-initials">{{ userInitials(user) }}</span>
                {{ user.name || user.userName || $t('golf.unassigned') }}
              </td>
              <td
                v-for="d in weekDays"
                :key="d.iso"
                class="week-cell"
                :class="{ 'bg-purple-light': isDateToday(d.date) }"
              >
                <div
                  v-for="a in assignmentsForUserOnDate(user.id, d.date)"
                  :key="'a'+a.id"
                  class="week-task-chip week-assignment-chip"
                  :class="assignmentChipClass()"
                  :style="assignmentChipStyle(a)"
                  @click.stop="onAssignmentChipClick(a, $event)"
                >{{ assignmentLabel(a) }}</div>
                <div
                  v-for="task in tasksForUserOnDate(user.id, d.date)"
                  :key="'t'+task.id"
                  class="week-task-chip"
                  @click="openEdit(task)"
                >{{ task.title }}</div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-if="equipmentRows.length === 0">
              <td :colspan="weekDays.length + 1" class="text-center text-muted small fst-italic py-3">{{ $t('golf.noToolReservations') }}</td>
            </tr>
            <tr v-for="tool in equipmentRows" :key="'wtool'+toolKey(tool.id)">
              <td class="week-label-col small fw-semibold">
                <i class="material-icons-round me-1" style="font-size:14px;vertical-align:middle;">build</i>{{ tool.name }}
              </td>
              <td
                v-for="d in weekDays"
                :key="d.iso"
                class="week-cell"
                :class="{ 'bg-purple-light': isDateToday(d.date) }"
                :title="(isAdmin && enableFutureReservation) ? $t('golf.clickToAddReservation') : ''"
                :style="(isAdmin && enableFutureReservation) ? 'cursor:pointer;' : ''"
                @click="onWeekToolCellClick(tool, d.date)"
              >
                <div
                  v-for="a in assignmentsForToolOnDate(tool.id, d.date)"
                  :key="'r'+a.id"
                  class="week-task-chip"
                  :class="a.active ? 'week-reservation-active-chip' : 'week-reservation-pending-chip'"
                  @click.stop="showBarDetail('assignment', a, $event)"
                >{{ a.project && a.project.name }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MONTH VIEW -->
    <div v-else-if="currentView === 'month'" class="card">
      <div class="table-responsive">
        <table class="table table-bordered mb-0 month-table">
          <thead>
            <tr>
              <th v-for="d in 7" :key="d" class="text-center small text-muted py-1">
                {{ monthDayName(d - 1) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(week, wi) in monthCalendar" :key="wi">
              <td
                v-for="(day, di) in week"
                :key="di"
                class="month-cell"
                :class="{ 'text-muted': !day || day.month !== currentDate.getMonth(), 'today-cell': day && isDateToday(day.date), 'month-cell-clickable': monthCreateEnabled && day }"
                :title="monthCreateEnabled && day ? $t('golf.clickToAddAssignment') : ''"
                @click="onMonthCellClick(day)"
              >
                <div v-if="day" class="month-day-number">{{ day.date.getDate() }}</div>
                <div v-if="day && axisView === 'user'">
                  <div
                    v-for="a in assignmentsOnDate(day.date)"
                    :key="'a'+a.id"
                    class="month-task-chip month-assignment-chip"
                    :class="assignmentChipClass()"
                    :style="assignmentChipStyle(a)"
                    @click.stop="onAssignmentChipClick(a, $event)"
                  >{{ assignmentLabel(a) }}</div>
                  <div
                    v-for="task in tasksOnDate(day.date)"
                    :key="'t'+task.id"
                    class="month-task-chip"
                    @click.stop="openEdit(task)"
                  >{{ task.title }}</div>
                </div>
                <div v-else-if="day">
                  <div
                    v-for="a in toolReservationsOnDate(day.date)"
                    :key="'r'+a.id"
                    class="month-task-chip"
                    :class="a.active ? 'month-reservation-active-chip' : 'month-reservation-pending-chip'"
                    :title="a.tool && a.tool.name"
                    @click.stop="showBarDetail('assignment', a, $event)"
                  >{{ a.tool && a.tool.name }}</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Routine Builder Modal -->
    <div v-if="showRoutineModal" class="modal-backdrop-custom" @click.self="closeRoutineModal">
      <div class="modal-dialog-custom card p-4" style="max-width:620px;">
        <h5 class="mb-3">{{ $t('golf.createRoutine') }}</h5>

        <div class="mb-3">
          <label class="form-label fw-semibold">{{ $t('golf.routineName') }}</label>
          <input v-model="routineForm.name" type="text" class="form-control" :placeholder="$t('golf.routineNamePlaceholder')" />
        </div>

        <div class="d-flex align-items-center justify-content-between mb-2">
          <span class="fw-semibold small">{{ $t('golf.routineItems') }}</span>
          <button class="btn btn-sm btn-outline-secondary" @click="addRoutineItem">
            <i class="material-icons-round me-1" style="font-size:15px;">add</i>{{ $t('golf.addItem') }}
          </button>
        </div>

        <div v-if="routineForm.items.length === 0" class="text-muted small mb-3">{{ $t('golf.noRoutineItems') }}</div>

        <div
          v-for="(item, idx) in routineForm.items"
          :key="idx"
          class="routine-item-row mb-2 p-2 rounded border"
        >
          <div class="row g-2 align-items-end">
            <div class="col-12 col-md-4">
              <label class="form-label small mb-1">{{ $t('golf.project') }}</label>
              <input v-model="item.projectSearch" type="text" class="form-control form-control-sm mb-1" :placeholder="$t('golf.searchProject')" autocomplete="off" />
              <select v-model="item.projectId" class="form-select form-select-sm">
                <option :value="null">— {{ $t('golf.selectProject') }} —</option>
                <option v-for="p in filteredProjectsForItem(item)" :key="projectIdValue(p.id)" :value="projectIdValue(p.id)">
                  {{ p.name || p.key }}
                </option>
              </select>
            </div>
            <div class="col-12 col-md-3">
              <label class="form-label small mb-1">{{ $t('golf.assignedTo') }}</label>
              <select v-model="item.assignedToUserId" class="form-select form-select-sm">
                <option :value="null">—</option>
                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name || u.userName }}</option>
              </select>
            </div>
            <div class="col-5 col-md-2">
              <label class="form-label small mb-1">{{ $t('golf.scheduledStart') }}</label>
              <input v-model="item.startTime" type="time" lang="en-GB" class="form-control form-control-sm" />
            </div>
            <div class="col-5 col-md-2">
              <label class="form-label small mb-1">{{ $t('golf.scheduledEnd') }}</label>
              <input v-model="item.endTime" type="time" lang="en-GB" class="form-control form-control-sm" />
            </div>
            <div class="col-2 col-md-1 d-flex align-items-end pb-1">
              <button class="btn btn-sm btn-link text-danger p-0" @click="removeRoutineItem(idx)">
                <i class="material-icons-round" style="font-size:18px;">close</i>
              </button>
            </div>
          </div>
        </div>

        <div class="d-flex gap-2 justify-content-end mt-3">
          <button class="btn btn-secondary" @click="closeRoutineModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-success"
            :disabled="!routineForm.name || routineForm.items.length === 0 || savingRoutine"
            @click="saveRoutine"
          >
            <span v-if="savingRoutine" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create / Edit Assignment Modal -->
    <div v-if="showAssignmentModal" class="modal-backdrop-custom" @click.self="closeAssignmentModal">
      <div class="modal-dialog-custom card p-4">
        <h5 class="mb-3">{{ editingAssignment ? $t('golf.editAssignment') : $t('golf.addAssignment') }}</h5>

        <div class="mb-3">
          <label class="form-label">{{ $t('golf.project') }}</label>
          <input v-model="projectSearch" type="text" class="form-control mb-1" :placeholder="$t('golf.searchProject')" autocomplete="off" />
          <select v-model="assignmentForm.projectId" class="form-select">
            <option :value="null">— {{ $t('golf.selectProject') }} —</option>
            <option v-for="p in filteredProjects" :key="projectIdValue(p.id)" :value="projectIdValue(p.id)">
              {{ p.name || p.key }}
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label class="form-label">{{ $t('golf.assignedTo') }}</label>
          <!-- Create: assign to one or more users at once — one assignment is created per selected user -->
          <el-select
            v-if="!editingAssignment"
            v-model="assignmentForm.userIds"
            multiple
            filterable
            clearable
            collapse-tags
            collapse-tags-tooltip
            class="w-100"
            :placeholder="$t('golf.unassigned')"
          >
            <el-option
              v-for="u in users"
              :key="u.id"
              :label="u.name || u.userName"
              :value="u.id"
            />
          </el-select>
          <!-- Edit: a single assignment always targets a single user -->
          <select v-else v-model="assignmentForm.userId" class="form-select">
            <option :value="null">— {{ $t('golf.unassigned') }} —</option>
            <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name || u.userName }}</option>
          </select>
        </div>

        <!-- Assignment types: multi-select on create (one assignment per checked type); immutable after create -->
        <div v-if="plannerConfigs.enableAssignmentTypes && !editingAssignment" class="mb-3">
          <label class="form-label">{{ $t('projectAssignments.types') }}</label>
          <div class="d-flex flex-wrap gap-3">
            <div v-for="t in projectAssignmentTypes" :key="t.id" class="form-check">
              <input :id="'planner-pat-' + t.id" v-model="assignmentForm.typeIds" class="form-check-input" type="checkbox" :value="t.id" />
              <label class="form-check-label" :for="'planner-pat-' + t.id">{{ t.type }}</label>
            </div>
          </div>
        </div>
        <div v-else-if="plannerConfigs.enableAssignmentTypes && editingAssignment && editingAssignment.projectAssignmentType" class="mb-3">
          <label class="form-label">{{ $t('projectAssignments.types') }}</label>
          <input type="text" class="form-control" :value="editingAssignment.projectAssignmentType.type" disabled />
        </div>

        <div class="row">
          <div class="col-12 mb-3">
            <label class="form-label">{{ $t('golf.scheduledStart') }}</label>
            <date-time-picker v-model="assignmentForm.startTime" @change="onAssignmentStartChanged" />
          </div>
          <!-- With status colors, EndTime means "actually finished at" (set by ending the assignment),
               so new assignments must be created without one — exactly like the old calendar. -->
          <div v-if="!(plannerConfigs.useStatusColors && !editingAssignment)" class="col-12 mb-3">
            <label class="form-label">{{ $t('golf.scheduledEnd') }}</label>
            <date-time-picker v-model="assignmentForm.endTime" />
          </div>
        </div>

        <div v-if="plannerConfigs.enableMultiplier" class="mb-3">
          <label class="form-label">{{ $t('projectAssignments.multiplier') }}</label>
          <input v-model="assignmentForm.multiplier" type="number" step="0.01" min="0" class="form-control" />
        </div>

        <div class="mb-3">
          <label class="form-label">{{ $t('common.comment') }}</label>
          <input v-model="assignmentForm.comment" type="text" class="form-control" />
        </div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary" @click="closeAssignmentModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-success"
            :disabled="!assignmentForm.projectId || !assignmentForm.startTime || missingTypeSelection || savingAssignment"
            @click="saveAssignment"
          >
            <span v-if="savingAssignment" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create / Edit Future Reservation Modal -->
    <div v-if="showReservationModal" class="modal-backdrop-custom" @click.self="closeReservationModal">
      <div class="modal-dialog-custom card p-4">
        <h5 class="mb-3">{{ editingReservation ? $t('common.edit') : $t('golf.addReservation') }}</h5>

        <div class="mb-3">
          <label class="form-label">{{ $t('golf.project') }}</label>
          <input v-model="reservationProjectSearch" type="text" class="form-control mb-1" :placeholder="$t('golf.searchProject')" autocomplete="off" />
          <select v-model="reservationForm.projectId" class="form-select">
            <option :value="null">— {{ $t('golf.selectProject') }} —</option>
            <option v-for="p in filteredReservationProjects" :key="projectIdValue(p.id)" :value="projectIdValue(p.id)">
              {{ p.name || p.key }}
            </option>
          </select>
        </div>

        <div class="row">
          <div class="col-12 mb-3">
            <label class="form-label">{{ $t('golf.scheduledStart') }} <span class="text-danger">*</span></label>
            <date-time-picker v-model="reservationForm.startTime" @change="onReservationStartChanged" />
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">{{ $t('golf.scheduledEnd') }} <span class="text-danger">*</span></label>
            <date-time-picker v-model="reservationForm.endTime" @change="onReservationTimeChanged" />
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">{{ $t('common.tool') }}</label>
          <div v-if="selectedReservationTool" class="d-flex align-items-center gap-2 p-2 border rounded">
            <img v-if="selectedReservationTool.image" :src="selectedReservationTool.image" alt="" style="width:48px;height:48px;object-fit:cover;border-radius:4px;" />
            <i v-else class="material-icons-round" style="font-size:32px;">build</i>
            <div class="flex-grow-1">
              <div class="fw-semibold small">{{ selectedReservationTool.name }}</div>
              <div v-if="selectedReservationTool.description" class="text-muted small">{{ selectedReservationTool.description }}</div>
            </div>
            <button class="btn btn-sm btn-outline-secondary" @click="openToolPickModal">
              <i class="material-icons-round" style="font-size:14px;">edit</i>
            </button>
          </div>
          <button
            v-else
            class="btn btn-outline-secondary w-100"
            :disabled="!reservationFormHasValidTimes"
            @click="openToolPickModal"
          >
            <i class="material-icons-round me-1" style="font-size:16px;vertical-align:middle;">build</i>
            {{ $t('projectAssignments.seeAvailableTools') }}
          </button>
          <div v-if="!reservationFormHasValidTimes" class="form-text small text-muted">
            {{ $t('golf.pickTimesFirst') }}
          </div>
        </div>

        <div v-if="selectedReservationTool && selectedReservationTool.multiTool" class="mb-3">
          <label class="form-label">{{ $t('tools.quantity') }}</label>
          <input v-model="reservationForm.quantity" type="number" min="1" class="form-control" />
        </div>

        <div class="mb-3">
          <label class="form-label">{{ $t('common.name') }}</label>
          <input v-model="reservationForm.comment" type="text" class="form-control" />
        </div>

        <div class="mb-3">
          <label class="form-label">{{ $t('users.email') }}</label>
          <input v-model="reservationForm.email" type="email" class="form-control" />
        </div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary" @click="closeReservationModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-success"
            :disabled="!reservationForm.projectId || !selectedReservationTool || !reservationFormHasValidTimes || savingReservation"
            @click="saveReservation"
          >
            <span v-if="savingReservation" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Pick Available Tool Sub-Modal -->
    <div v-if="showToolPickModal" class="modal-backdrop-custom" style="z-index:1060;" @click.self="closeToolPickModal">
      <div class="modal-dialog-custom card p-4" style="max-width:640px;">
        <h5 class="mb-3">{{ $t('tools.pickTool') }}</h5>

        <div class="row g-2 mb-3">
          <div class="col-md-7">
            <el-input
              v-model="availableToolsSearch"
              class="search-input"
              clearable
              prefix-icon="Search"
              :placeholder="$t('common.searchRecords')"
              @input="onAvailableToolsSearchInput"
            />
          </div>
          <div class="col-md-5">
            <select v-model="availableToolTypeFilter" class="form-select" @change="fetchAvailableTools(1)">
              <option value="">{{ $t('tools.toolTypeStatus') }}</option>
              <option v-for="tt in availableToolTypes" :key="tt.id" :value="tt.id">{{ tt.type }}</option>
            </select>
          </div>
        </div>

        <div v-if="availableToolsLoading" class="text-center py-4">
          <div class="spinner-border text-purple"></div>
        </div>
        <div v-else-if="availableTools.length === 0" class="text-center text-muted small fst-italic py-4">
          {{ $t('golf.noAvailableTools') }}
        </div>
        <div v-else class="tool-pick-list">
          <div
            v-for="t in availableTools"
            :key="toolKey(t.id)"
            class="tool-pick-row d-flex align-items-center gap-2 p-2 border-bottom"
            :class="{ 'tool-pick-row-selected': pendingPickedTool && toolKey(pendingPickedTool.id) === toolKey(t.id) }"
            @click="pendingPickedTool = t"
          >
            <img v-if="t.image" :src="t.image" alt="" style="width:56px;height:56px;object-fit:cover;border-radius:4px;" />
            <i v-else class="material-icons-round text-muted" style="font-size:40px;">build</i>
            <div class="flex-grow-1">
              <div class="fw-semibold small">{{ t.name }}</div>
              <div v-if="t.description" class="text-muted small">{{ t.description }}</div>
            </div>
            <input
              type="radio"
              class="form-check-input"
              :checked="pendingPickedTool && toolKey(pendingPickedTool.id) === toolKey(t.id)"
              @click.stop="pendingPickedTool = t"
            />
          </div>
        </div>

        <div v-if="availableTools.length > 0" class="d-flex justify-content-between align-items-center mt-2 small">
          <span class="text-muted">
            {{ $t('pagination.showing') }} {{ availableToolsFrom + 1 }} {{ $t('pagination.to') }} {{ availableToolsTo }} {{ $t('pagination.of') }} {{ availableToolsPagination.total }}
          </span>
          <div class="btn-group btn-group-sm">
            <button class="btn btn-outline-secondary" :disabled="availableToolsPagination.currentPage <= 1" @click="fetchAvailableTools(availableToolsPagination.currentPage - 1)">
              <i class="material-icons-round" style="font-size:14px;">chevron_left</i>
            </button>
            <button class="btn btn-outline-secondary" disabled>{{ availableToolsPagination.currentPage }} / {{ availableToolsTotalPages }}</button>
            <button class="btn btn-outline-secondary" :disabled="availableToolsPagination.currentPage >= availableToolsTotalPages" @click="fetchAvailableTools(availableToolsPagination.currentPage + 1)">
              <i class="material-icons-round" style="font-size:14px;">chevron_right</i>
            </button>
          </div>
        </div>

        <div class="d-flex gap-2 justify-content-end mt-3">
          <button class="btn btn-secondary" @click="closeToolPickModal">{{ $t('common.cancel') }}</button>
          <button class="btn btn-success" :disabled="!pendingPickedTool" @click="confirmToolPick">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- Detail popup (click on bar) -->
    <div
      v-if="detailPopup"
      ref="detailPopupEl"
      class="bar-detail-popup card shadow"
      :style="{ top: detailPopup.y + 'px', left: detailPopup.x + 'px' }"
      @click.stop
    >
      <div class="card-body p-3">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <span
            class="badge me-2"
            :class="detailPopup.type === 'assignment' ? 'bg-primary' : 'bg-secondary'"
          >
            {{ detailPopup.type === 'assignment' ? $t('golf.projectAssignments') : $t('golf.tasks') }}
          </span>
          <button class="btn-close btn-close-sm ms-auto" @click="detailPopup = null"></button>
        </div>

        <template v-if="detailPopup.type === 'assignment'">
          <div class="fw-bold mb-1">{{ detailPopup.data.project && detailPopup.data.project.name }}</div>
          <div v-if="detailPopup.data.projectAssignmentType" class="small text-muted">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">category</i>
            {{ detailPopup.data.projectAssignmentType.type }}
          </div>
          <div v-if="detailPopup.data.tool" class="small text-muted">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">build</i>
            {{ detailPopup.data.tool.name }}
          </div>
          <div v-if="detailPopup.data.user" class="small text-muted">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">person</i>
            {{ detailPopup.data.user.firstName || detailPopup.data.user.userName }}
          </div>
          <div class="small text-muted mt-1">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">schedule</i>
            {{ formatBarTime(detailPopup.data.startTime) }}
            <template v-if="detailPopup.data.endTime"> – {{ formatBarTime(detailPopup.data.endTime) }}</template>
          </div>
          <div v-if="detailPopup.data.comment" class="small text-muted mt-1">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">comment</i>
            {{ detailPopup.data.comment }}
          </div>
          <div v-if="detailPopup.data.externalUserEmail" class="small text-muted mt-1">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">email</i>
            {{ detailPopup.data.externalUserEmail }}
          </div>
          <div v-if="plannerConfigs.enableMultiplier && detailPopup.data.multiplier != null" class="small text-muted mt-1">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">close</i>
            {{ detailPopup.data.multiplier }}
          </div>
          <div v-if="!detailPopup.data.tool && plannerConfigs.useStatusColors" class="mt-2">
            <span class="badge" :style="{ background: statusColor(detailPopup.data) }">{{ statusLabel(detailPopup.data) }}</span>
          </div>
          <div v-if="detailPopup.data.tool" class="mt-2">
            <span class="badge" :class="detailPopup.data.active ? 'bg-success' : 'bg-warning text-dark'">
              {{ detailPopup.data.active ? $t('golf.confirmedReservation') : $t('golf.pendingReservation') }}
            </span>
          </div>
          <div v-if="axisView === 'equipment' && isAdmin && detailPopup.data.tool" class="d-flex gap-2 mt-2">
            <button
              v-if="!detailPopup.data.active"
              class="btn btn-sm btn-success flex-grow-1"
              :disabled="confirmingReservation"
              @click="confirmReservation(detailPopup.data)"
            >
              <i class="material-icons-round me-1" style="font-size:14px;">check</i>{{ $t('common.confirm') }}
            </button>
            <button
              v-if="!detailPopup.data.active"
              class="btn btn-sm btn-primary"
              @click="openEditReservation(detailPopup.data)"
            >
              <i class="material-icons-round" style="font-size:14px;">edit</i>
            </button>
            <button
              class="btn btn-sm btn-outline-danger"
              @click="deleteReservation(detailPopup.data)"
            >
              <i class="material-icons-round" style="font-size:14px;">delete</i>
            </button>
          </div>
          <div v-if="axisView === 'user' && isAdmin && !detailPopup.data.tool" class="d-flex gap-2 mt-2">
            <button
              class="btn btn-sm btn-primary flex-grow-1"
              @click="openEditAssignment(detailPopup.data)"
            >
              <i class="material-icons-round me-1" style="font-size:14px;">edit</i>{{ $t('common.edit') }}
            </button>
            <button
              class="btn btn-sm btn-outline-danger"
              :disabled="deletingAssignment"
              @click="deleteAssignment(detailPopup.data)"
            >
              <i class="material-icons-round" style="font-size:14px;">delete</i>
            </button>
          </div>
        </template>

        <template v-else>
          <div class="fw-bold mb-1">{{ detailPopup.data.title }}</div>
          <div v-if="detailPopup.data.description" class="small text-muted mb-1">{{ detailPopup.data.description }}</div>
          <div v-if="detailPopup.data.assignedToName" class="small text-muted">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">person</i>
            {{ detailPopup.data.assignedToName }}
          </div>
          <div class="small text-muted mt-1">
            <i class="material-icons-round" style="font-size:13px;vertical-align:middle;">schedule</i>
            {{ formatBarTime(detailPopup.data.scheduledStart) }}
            <template v-if="detailPopup.data.scheduledEnd"> – {{ formatBarTime(detailPopup.data.scheduledEnd) }}</template>
          </div>
          <div class="mt-2">
            <span class="badge" :class="detailPopup.data.isCompleted ? 'bg-success' : 'bg-warning text-dark'">
              {{ detailPopup.data.isCompleted ? $t('golf.completed') : $t('golf.inProgress') }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <!-- Schedule / Edit Task Modal -->
    <div v-if="showModal" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-dialog-custom card p-4">
        <h5 class="mb-3">{{ editingTask ? $t('golf.editTask') : $t('golf.scheduleTask') }}</h5>

        <div v-if="!editingTask" class="mb-3">
          <label class="form-label">{{ $t('golf.selectTaskToSchedule') }}</label>
          <select v-model="form.taskId" class="form-select">
            <option :value="null">— {{ $t('golf.selectTask') }} —</option>
            <option v-for="t in schedulableTasks" :key="t.id" :value="t.id">
              {{ t.title }}{{ t.holeNumber ? ` (#${t.holeNumber})` : '' }}
            </option>
          </select>
          <small v-if="schedulableTasks.length === 0" class="text-muted">
            {{ $t('golf.noTasksToSchedule') }}
          </small>
        </div>

        <div v-if="selectedTask" class="mb-3 p-2 rounded bg-light">
          <div class="fw-bold">{{ selectedTask.title }}</div>
          <div v-if="selectedTask.description" class="small text-muted mt-1">
            {{ selectedTask.description }}
          </div>
          <div v-if="selectedTask.holeNumber" class="small text-muted mt-1">
            {{ $t('golf.hole') }} #{{ selectedTask.holeNumber }}
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">{{ $t('golf.assignedTo') }}</label>
          <select v-model="form.assignedToUserId" class="form-select">
            <option :value="null">— {{ $t('golf.unassigned') }} —</option>
            <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name || u.userName }}</option>
          </select>
        </div>

        <div class="row">
          <div class="col-12 mb-3">
            <label class="form-label">{{ $t('golf.scheduledStart') }}</label>
            <date-time-picker v-model="form.scheduledStart" @change="onTaskStartChanged" />
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">{{ $t('golf.scheduledEnd') }}</label>
            <date-time-picker v-model="form.scheduledEnd" />
          </div>
        </div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary" @click="closeModal">{{ $t('common.cancel') }}</button>
          <button
            class="btn btn-success"
            :disabled="!form.taskId || saving"
            @click="saveTask"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { golfApi, default as mainApi } from "../../services/api";
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import swal from 'sweetalert2';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import DateTimePicker from '../../components/DateTimePicker.vue';

const HOUR_WIDTH = 60; // px per hour

// Palette for project assignment bars based on project id
const PROJECT_COLORS = [
  '#2196F3','#4CAF50','#FF9800','#9C27B0','#00BCD4',
  '#F44336','#009688','#673AB7','#3F51B5','#E91E63',
];

function projectColor(projectId) {
  if (!projectId) return PROJECT_COLORS[0];
  const id = typeof projectId === 'object' ? projectId.value || '' : String(projectId);
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) & 0xffffffff;
  return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length];
}

function toLocalDatetimeInput(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDatetimeInput(str) {
  if (!str) return null;
  return new Date(str).toISOString();
}

// Format a Date as YYYY-MM-DD using its LOCAL components. Using toISOString().slice(0,10)
// converts to UTC first, which yields the previous/next day for users in non-UTC timezones
// when the Date is at local midnight — and silently drops the day's data from API queries.
function toLocalDateString(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Pull a user-readable message out of an axios error response. Ardalis.Result.Invalid
// returns an array of { errorMessage }; bare strings and ProblemDetails-style objects also occur.
function extractApiError(e) {
  const data = e?.response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) {
    const msg = data.map(v => v?.errorMessage || v?.ErrorMessage || v?.message).filter(Boolean).join(' • ');
    return msg || null;
  }
  if (Array.isArray(data.errors)) {
    const msg = data.errors.map(v => v?.errorMessage || v?.ErrorMessage || v?.message).filter(Boolean).join(' • ');
    if (msg) return msg;
  }
  return data.errorMessage || data.message || data.title || data.detail || null;
}

export default {
  name: 'Planner',
  components: { DateTimePicker },
  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      currentView: 'day',
      currentDate: new Date(today),
      // GolfTasks (ad-hoc)
      tasks: [],
      // ProjectAssignments (primary)
      projectAssignments: [],
      // Projects list (for routine builder dropdown)
      projects: [],
      users: [],
      userImages: {}, // userId -> object URL of the lazily fetched thumbnail
      toolImages: {}, // toolKey -> object URL of the lazily fetched thumbnail (false = fetched, no image)
      loading: false,
      saving: false,
      showModal: false,
      editingTask: null,
      form: { taskId: null, assignedToUserId: null, scheduledStart: '', scheduledEnd: '' },
      // Assignment modal
      showAssignmentModal: false,
      savingAssignment: false,
      editingAssignment: null,
      deletingAssignment: false,
      assignmentForm: { projectId: null, userId: null, userIds: [], typeIds: [], startTime: '', endTime: '', comment: '', multiplier: null },
      dragTask: null,
      dragAssignment: null,
      dragGrabOffsetPx: 0,
      dragOverUser: undefined,
      detailPopup: null,
      hourWidth: HOUR_WIDTH,
      views: [
        { key: 'day',   label: 'golf.dayView' },
        { key: 'week',  label: 'golf.weekView' },
        { key: 'month', label: 'golf.monthView' },
      ],
      axes: [
        { key: 'user',      label: 'golf.userView',      icon: 'person' },
        { key: 'equipment', label: 'golf.equipmentView', icon: 'build' },
      ],
      axisView: 'user',
      // Routines
      routines: [],
      selectedRoutineId: null,
      applyingRoutine: false,
      showRoutineModal: false,
      savingRoutine: false,
      routineForm: { name: '', items: [] },
      // Project search
      projectSearch: '',
      // Guards the currentView/currentDate watchers so setting the configured
      // default view during mount doesn't trigger a duplicate fetch
      initialized: false,
      // Per-customer planner configuration — every value defaults to the
      // pre-existing behavior so customers without these configs are unaffected
      plannerConfigs: {
        onlyShowUsersWithAssignments: false,
        enableMonthViewCreation: false,
        enableAssignmentTypes: false,
        enableMultiplier: false,
        disableRoutines: false,
        disableTaskCreation: false,
        disableTools: false,
        useStatusColors: false,
        usePreCreatedFiltering: false,
        onlyShowPreCreated: false,
      },
      projectAssignmentTypes: [],
      // Future reservation feature
      enableFutureReservation: false,
      showReservationModal: false,
      savingReservation: false,
      confirmingReservation: false,
      reservationProjectSearch: '',
      reservationForm: { projectId: null, startTime: '', endTime: '', comment: '', email: '', quantity: null },
      selectedReservationTool: null,
      editingReservation: null,
      // Tool pick sub-modal
      showToolPickModal: false,
      availableTools: [],
      availableToolsLoading: false,
      availableToolsSearch: '',
      availableToolsSearchTimer: null,
      availableToolTypes: [],
      availableToolTypeFilter: '',
      availableToolsPagination: { perPage: 5, currentPage: 1, total: 0 },
      pendingPickedTool: null,
    };
  },
  computed: {
    isAdmin() {
      try {
        const role = jwtDecode(Cookies.get('accessToken')).role;
        return role === 'Admin' || role === 'SuperAdmin';
      } catch { return false; }
    },
    hours() {
      return Array.from({ length: 24 }, (_, i) => i);
    },
    totalWidth() {
      return 24 * this.hourWidth;
    },
    headingLabel() {
      if (this.currentView === 'day') {
        return this.currentDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      }
      if (this.currentView === 'week') {
        const start = this.weekStart;
        const end = new Date(start); end.setDate(end.getDate() + 6);
        return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
      return this.currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    },
    weekStart() {
      const d = new Date(this.currentDate);
      const day = d.getDay(); // 0=Sun
      d.setDate(d.getDate() - day);
      return d;
    },
    weekDays() {
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(this.weekStart);
        d.setDate(d.getDate() + i);
        return {
          date: d,
          iso: toLocalDateString(d),
          weekday: d.toLocaleDateString(undefined, { weekday: 'short' }),
          label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        };
      });
    },
    monthCalendar() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const startDay = first.getDay();
      const weeks = [];
      let week = [];
      for (let i = 0; i < startDay; i++) {
        const d = new Date(year, month, 1 - startDay + i);
        week.push({ date: d, month: d.getMonth() });
      }
      for (let d = 1; d <= last.getDate(); d++) {
        const date = new Date(year, month, d);
        week.push({ date, month });
        if (week.length === 7) { weeks.push(week); week = []; }
      }
      while (week.length > 0 && week.length < 7) {
        const d = new Date(year, month + 1, week.length - (last.getDay()));
        week.push({ date: d, month: d.getMonth() });
      }
      if (week.length) weeks.push(week);
      return weeks;
    },
    allRows() {
      const unassigned = { id: null, name: this.$t('golf.unassigned'), userName: '' };
      return [unassigned, ...this.visibleUsers];
    },
    visibleUsers() {
      if (!this.plannerConfigs.onlyShowUsersWithAssignments) return this.users;
      const byId = new Map();
      const assignmentSource = this.currentView === 'day' ? this.dayAssignments : this.projectAssignments;
      for (const a of assignmentSource) {
        if (a.userId && !byId.has(a.userId)) byId.set(a.userId, a.user || null);
      }
      // Keep rows for users that only have scheduled tasks so their bars stay visible
      const taskSource = this.currentView === 'day' ? this.dayTasks : this.tasks;
      for (const t of taskSource) {
        if (t.assignedToUserId && !byId.has(t.assignedToUserId)) byId.set(t.assignedToUserId, null);
      }
      const rows = this.users.filter(u => byId.has(u.id));
      // Users excluded from the /users list (e.g. SuperAdmins) still get a row when they
      // have assignments in the period — synthesized from the assignment's embedded user
      const known = new Set(this.users.map(u => u.id));
      for (const [id, user] of byId) {
        if (known.has(id)) continue;
        const name = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName : '';
        rows.push({ id, name, userName: (user && user.userName) || '' });
      }
      return rows;
    },
    monthCreateEnabled() {
      return this.isAdmin && this.axisView === 'user' && this.plannerConfigs.enableMonthViewCreation;
    },
    missingTypeSelection() {
      return this.plannerConfigs.enableAssignmentTypes && !this.editingAssignment && this.assignmentForm.typeIds.length === 0;
    },
    filteredProjects() {
      if (!this.projectSearch) return this.projects;
      const q = this.projectSearch.toLowerCase();
      return this.projects.filter(p => (p.name || p.key || '').toLowerCase().includes(q));
    },
    dayTasks() {
      const dayStart = new Date(this.currentDate); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.tasks.filter(t => {
        if (!t.scheduledStart) return false;
        const s = new Date(t.scheduledStart);
        return s >= dayStart && s < dayEnd;
      });
    },
    dayAssignments() {
      // User-axis collection — tool reservations belong to the equipment axis only
      const dayStart = new Date(this.currentDate); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.projectAssignments.filter(a => {
        if (a.tool || a.toolId) return false;
        const s = new Date(a.startTime);
        return s >= dayStart && s < dayEnd;
      });
    },
    unassignedDayTasks() {
      return this.dayTasks.filter(t => !t.assignedToUserId);
    },
    selectedTask() {
      if (!this.form.taskId) return null;
      return this.tasks.find(t => t.id === this.form.taskId) || null;
    },
    schedulableTasks() {
      return this.tasks.filter(t => !t.isCompleted);
    },
    unassignedDayAssignments() {
      return this.dayAssignments.filter(a => !a.userId);
    },
    // Stack overlapping same-row bars into lanes so simultaneous assignments
    // (e.g. one per assignment type) don't render on top of each other in the day view
    dayLanes() {
      const lanes = {};
      const counts = {};
      const rows = new Map();
      for (const a of this.dayAssignments) {
        const key = a.userId || 'unassigned';
        if (!rows.has(key)) rows.set(key, []);
        rows.get(key).push(a);
      }
      const MIN_BAR_MS = 3600000; // endless assignments render one hour wide
      for (const [key, list] of rows) {
        const sorted = [...list].sort((x, y) => new Date(x.startTime) - new Date(y.startTime));
        const laneEnds = [];
        for (const a of sorted) {
          const start = new Date(a.startTime).getTime();
          const end = Math.max(a.endTime ? new Date(a.endTime).getTime() : 0, start + MIN_BAR_MS);
          let lane = laneEnds.findIndex(e => e <= start);
          if (lane === -1) {
            lane = laneEnds.length;
            laneEnds.push(end);
          } else {
            laneEnds[lane] = end;
          }
          lanes[a.id] = lane;
        }
        counts[key] = laneEnds.length;
      }
      return { lanes, counts };
    },
    // ── Equipment axis ──
    toolReservations() {
      // Project assignments that target a tool (i.e. tool reservations)
      return this.projectAssignments.filter(a => a.tool || a.toolId);
    },
    dayToolReservations() {
      const dayStart = new Date(this.currentDate); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.toolReservations.filter(a => {
        const s = new Date(a.startTime);
        return s >= dayStart && s < dayEnd;
      });
    },
    equipmentRows() {
      // Unique tools present in current period's tool reservations
      const seen = new Map();
      const source = this.currentView === 'day' ? this.dayToolReservations : this.toolReservations;
      for (const a of source) {
        const tool = a.tool;
        if (!tool) continue;
        const k = this.toolKey(tool.id);
        if (!seen.has(k)) seen.set(k, { id: tool.id, name: tool.name });
      }
      return Array.from(seen.values()).sort((x, y) => (x.name || '').localeCompare(y.name || ''));
    },
    filteredReservationProjects() {
      if (!this.reservationProjectSearch) return this.projects;
      const q = this.reservationProjectSearch.toLowerCase();
      return this.projects.filter(p => (p.name || p.key || '').toLowerCase().includes(q));
    },
    reservationFormHasValidTimes() {
      if (!this.reservationForm.startTime || !this.reservationForm.endTime) return false;
      return new Date(this.reservationForm.startTime) < new Date(this.reservationForm.endTime);
    },
    availableToolsFrom() {
      return this.availableToolsPagination.perPage * (this.availableToolsPagination.currentPage - 1);
    },
    availableToolsTo() {
      const high = this.availableToolsFrom + this.availableToolsPagination.perPage;
      return this.availableToolsPagination.total < high ? this.availableToolsPagination.total : high;
    },
    availableToolsTotalPages() {
      return this.availableToolsPagination.total > 0
        ? Math.ceil(this.availableToolsPagination.total / this.availableToolsPagination.perPage)
        : 1;
    },
  },
  watch: {
    currentView() { if (this.initialized) this.fetchAll(); },
    currentDate() { if (this.initialized) this.fetchAll(); },
    axisView(v) { if (v === 'equipment') this.fetchToolImages(); },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick, true);
    Object.values(this.userImages).forEach(url => URL.revokeObjectURL(url));
    Object.values(this.toolImages).forEach(url => { if (url) URL.revokeObjectURL(url); });
  },
  async mounted() {
    document.addEventListener('click', this.onDocumentClick, true);
    await Promise.all([this.fetchUsers(), this.loadPlannerConfigs()]);
    const tasks = [this.fetchAll(), this.fetchProjects()];
    if (!this.plannerConfigs.disableRoutines) tasks.push(this.fetchRoutines());
    if (this.plannerConfigs.enableAssignmentTypes) tasks.push(this.fetchProjectAssignmentTypes());
    await Promise.all(tasks);
    this.initialized = true;
  },
  methods: {
    async fetchUsers() {
      try {
        const res = await golfApi.get('/users');
        this.users = res.data;
        this.fetchUserImages();
      } catch (e) {
        console.error('Failed to load users:', e); // eslint-disable-line no-console
      }
    },
    async fetchUserImages() {
      const withImages = this.users.filter(u => u.hasImage && !this.userImages[u.id]);
      await Promise.all(withImages.map(async (u) => {
        try {
          const res = await golfApi.get(`/users/${u.id}/image`, { responseType: 'blob' });
          this.userImages[u.id] = URL.createObjectURL(res.data);
        } catch (e) {
          console.error('Failed to load user image:', e); // eslint-disable-line no-console
        }
      }));
    },
    userInitials(user) {
      const name = (user.name || user.userName || '').trim();
      if (!name) return '?';
      const parts = name.split(/\s+/);
      return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
    },
    async fetchProjects() {
      try {
        let url = '/Project?pageSize=200';
        if (this.plannerConfigs.usePreCreatedFiltering) url += '&preCreatedAssignments=true';
        const res = await mainApi.get(url);
        // PaginatedList response shape: { result: [...], totalItems, ... }
        this.projects = res.data.result || res.data || [];
      } catch (e) {
        console.error('Failed to load projects:', e); // eslint-disable-line no-console
      }
    },
    projectIdValue(id) {
      // ProjectId is a strongly-typed ID; serialised as { value: '...' } from .NET
      if (!id) return null;
      return typeof id === 'object' ? id.value : id;
    },
    toolKey(id) {
      // ToolId is a strongly-typed ID; serialised as { value: '...' } from .NET
      if (id == null) return null;
      return typeof id === 'object' ? (id.value ?? '') : String(id);
    },
    async loadPlannerConfigs() {
      try {
        const [
          futureReservation, defaultView, onlyUsersWithAssignments, monthViewCreation,
          assignmentTypes, multiplier, disableRoutines, disableTaskCreation, disableTools, statusColors, preCreatedFiltering, onlyShowPreCreated,
        ] = await Promise.all([
          getCustomerConfig('EnableFutureReservationProjectAssignments'),
          getCustomerConfig('PlannerDefaultView'),
          getCustomerConfig('OnlyShowUsersWithAssignmentsInPlanner'),
          getCustomerConfig('EnableAssignmentCreationInPlannerMonthView'),
          getCustomerConfig('EnableProjectAssignmentTypes'),
          getCustomerConfig('EnableProjectAssignmentMultiplierInPlanner'),
          getCustomerConfig('DisableRoutinesInPlanner'),
          getCustomerConfig('DisableTaskCreationInPlanner'),
          getCustomerConfig('DisableToolsInPlanner'),
          getCustomerConfig('UseAssignmentStatusColorsInPlanner'),
          getCustomerConfig('UsePreCreatedAssignmentsFilteringInPlanner'),
          getCustomerConfig('OnlyShowPreCreatedAssignmentsInPlanner'),
        ]);
        this.enableFutureReservation = futureReservation === true;
        this.plannerConfigs = {
          onlyShowUsersWithAssignments: onlyUsersWithAssignments === true,
          enableMonthViewCreation: monthViewCreation === true,
          enableAssignmentTypes: assignmentTypes === true,
          enableMultiplier: multiplier === true,
          disableRoutines: disableRoutines === true,
          disableTaskCreation: disableTaskCreation === true,
          disableTools: disableTools === true,
          useStatusColors: statusColors === true,
          usePreCreatedFiltering: preCreatedFiltering === true,
          onlyShowPreCreated: onlyShowPreCreated === true,
        };
        const view = String(defaultView || '').toLowerCase();
        if (view === 'week' || view === 'month') this.currentView = view;
      } catch (e) {
        console.error('Failed to load planner configs:', e); // eslint-disable-line no-console
        this.enableFutureReservation = false;
      }
      if ((!this.enableFutureReservation || this.plannerConfigs.disableTools) && this.axisView === 'equipment') {
        this.axisView = 'user';
      }
    },
    async fetchProjectAssignmentTypes() {
      try {
        const res = await mainApi.get('/ProjectAssignmentType');
        this.projectAssignmentTypes = res.data || [];
      } catch (e) {
        console.error('Failed to load project assignment types:', e); // eslint-disable-line no-console
      }
    },
    async fetchAvailableToolTypes() {
      if (this.availableToolTypes.length > 0) return;
      try {
        const res = await mainApi.get('/ToolType');
        this.availableToolTypes = res.data || [];
      } catch (e) {
        console.error('Failed to load tool types:', e); // eslint-disable-line no-console
      }
    },
    dateRange() {
      let from, to;
      if (this.currentView === 'day') {
        from = new Date(this.currentDate);
        to = new Date(this.currentDate);
      } else if (this.currentView === 'week') {
        from = new Date(this.weekStart);
        to = new Date(this.weekStart); to.setDate(to.getDate() + 6);
      } else {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        from = new Date(year, month, 1);
        to = new Date(year, month + 1, 0);
      }
      return {
        from: toLocalDateString(from),
        to: toLocalDateString(to),
      };
    },
    async fetchAll() {
      this.loading = true;
      try {
        const fetches = [this.fetchProjectAssignments()];
        // Tasks are golf-specific — customers with task creation disabled never see them
        if (!this.plannerConfigs.disableTaskCreation) fetches.push(this.fetchTasks());
        await Promise.all(fetches);
      } finally {
        this.loading = false;
      }
    },
    async fetchTasks() {
      try {
        let url = '/tasks?includeCompleted=true';
        if (this.currentView === 'day') {
          url += `&date=${toLocalDateString(this.currentDate)}`;
        }
        const res = await golfApi.get(url);
        this.tasks = res.data;
      } catch (e) {
        console.error('Failed to load tasks:', e); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
    async fetchProjectAssignments() {
      try {
        const { from, to } = this.dateRange();
        let url = `/Project/Assignments?fromDate=${from}&toDate=${to}`;
        // Calendar-parity mode: hide ad-hoc (non-pre-created) assignments, e.g. the
        // instantly-completed rows the today's-assignments checklist creates
        if (this.plannerConfigs.onlyShowPreCreated) url += '&preCreated=true&projectAssignmentWithTools=false';
        const res = await mainApi.get(url);
        this.projectAssignments = res.data || [];
        this.fetchToolImages();
      } catch (e) {
        console.error('Failed to load project assignments:', e); // eslint-disable-line no-console
      }
    },
    async fetchToolImages() {
      // Thumbnails only show in the day view equipment axis — lazy-load just those rows
      if (this.axisView !== 'equipment' || this.currentView !== 'day') return;
      const pending = this.equipmentRows.filter(t => this.toolImages[this.toolKey(t.id)] === undefined);
      await Promise.all(pending.map(async (t) => {
        const k = this.toolKey(t.id);
        this.toolImages[k] = false; // mark as fetched so tools without an image aren't re-requested
        try {
          const res = await mainApi.get(`/Tool/${k}/Thumbnail`, { responseType: 'blob' });
          if (res.status === 200 && res.data && res.data.size > 0) {
            this.toolImages[k] = URL.createObjectURL(res.data);
          }
        } catch (e) {
          console.error('Failed to load tool image:', e); // eslint-disable-line no-console
        }
      }));
    },
    navigate(dir) {
      const d = new Date(this.currentDate);
      if (this.currentView === 'day')   d.setDate(d.getDate() + dir);
      if (this.currentView === 'week')  d.setDate(d.getDate() + dir * 7);
      if (this.currentView === 'month') d.setMonth(d.getMonth() + dir);
      this.currentDate = d;
    },
    goToday() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.currentDate = today;
    },
    isDateToday(date) {
      const today = new Date();
      return date.getFullYear() === today.getFullYear()
        && date.getMonth() === today.getMonth()
        && date.getDate() === today.getDate();
    },
    monthDayName(i) {
      const d = new Date(2023, 0, 1 + i);
      return d.toLocaleDateString(undefined, { weekday: 'short' });
    },
    tasksForUser(userId) {
      return this.dayTasks.filter(t => t.assignedToUserId === userId);
    },
    assignmentsForUser(userId) {
      return this.dayAssignments.filter(a => a.userId === userId);
    },
    assignmentsForTool(toolId) {
      const k = this.toolKey(toolId);
      return this.dayToolReservations.filter(a => this.toolKey(a.toolId || (a.tool && a.tool.id)) === k);
    },
    assignmentsForToolOnDate(toolId, date) {
      const k = this.toolKey(toolId);
      const dayStart = new Date(date); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.toolReservations.filter(a => {
        if (this.toolKey(a.toolId || (a.tool && a.tool.id)) !== k) return false;
        const s = new Date(a.startTime);
        return s >= dayStart && s < dayEnd;
      });
    },
    toolReservationsOnDate(date) {
      const dayStart = new Date(date); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.toolReservations.filter(a => {
        const s = new Date(a.startTime);
        return s >= dayStart && s < dayEnd;
      });
    },
    tasksForUserOnDate(userId, date) {
      const dayStart = new Date(date); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.tasks.filter(t => {
        if (t.assignedToUserId !== userId) return false;
        if (!t.scheduledStart) return false;
        const s = new Date(t.scheduledStart);
        return s >= dayStart && s < dayEnd;
      });
    },
    assignmentsForUserOnDate(userId, date) {
      const dayStart = new Date(date); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.projectAssignments.filter(a => {
        if (a.tool || a.toolId) return false;
        if (a.userId !== userId) return false;
        const s = new Date(a.startTime);
        return s >= dayStart && s < dayEnd;
      });
    },
    tasksOnDate(date) {
      const dayStart = new Date(date); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.tasks.filter(t => {
        if (!t.scheduledStart) return false;
        const s = new Date(t.scheduledStart);
        return s >= dayStart && s < dayEnd;
      });
    },
    assignmentsOnDate(date) {
      const dayStart = new Date(date); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
      return this.projectAssignments.filter(a => {
        if (a.tool || a.toolId) return false;
        const s = new Date(a.startTime);
        return s >= dayStart && s < dayEnd;
      });
    },
    // Same derivation as the pre-created assignments view: EndTime means "actually finished at"
    assignmentStatus(assignment) {
      if (assignment.active) return 'ongoing';
      if (!assignment.endTime) return 'pending';
      return 'finished';
    },
    statusColor(assignment) {
      const status = this.assignmentStatus(assignment);
      if (status === 'ongoing') return '#4CAF50';
      if (status === 'finished') return '#2196F3';
      return '#F44336';
    },
    statusLabel(assignment) {
      const status = this.assignmentStatus(assignment);
      if (status === 'ongoing') return this.$t('projectAssignments.ongoing');
      if (status === 'finished') return this.$t('projectAssignments.finished');
      return this.$t('projectAssignments.notFinished');
    },
    assignmentLabel(a) {
      const name = (a.project && a.project.name) || '';
      if (this.plannerConfigs.enableAssignmentTypes && a.projectAssignmentType) {
        return `${name} – ${a.projectAssignmentType.type}`;
      }
      return name;
    },
    assignmentChipClass() {
      return { 'chip-clickable': this.plannerConfigs.enableMonthViewCreation };
    },
    assignmentChipStyle(a) {
      // Person assignments only — tool reservation chips keep their own status classes
      if (this.plannerConfigs.useStatusColors && !a.tool) {
        return { background: this.statusColor(a) };
      }
      return {};
    },
    assignmentBarStyle(assignment) {
      const start = new Date(assignment.startTime);
      const dayStart = new Date(this.currentDate); dayStart.setHours(0,0,0,0);
      const startMins = (start - dayStart) / 60000;
      const left = startMins * (this.hourWidth / 60);

      let width = this.hourWidth;
      if (assignment.endTime) {
        const end = new Date(assignment.endTime);
        const durationMins = (end - start) / 60000;
        width = Math.max(durationMins * (this.hourWidth / 60), 20);
      }

      const projectId = assignment.projectId && (assignment.projectId.value || assignment.projectId);
      const bg = this.plannerConfigs.useStatusColors && !assignment.tool
        ? this.statusColor(assignment)
        : projectColor(projectId);
      const lane = this.axisView === 'user' ? (this.dayLanes.lanes[assignment.id] || 0) : 0;
      return { left: left + 'px', width: width + 'px', background: bg, top: (4 + lane * 32) + 'px', height: '28px' };
    },
    rowMinHeight(userId) {
      const laneCount = Math.max(1, this.dayLanes.counts[userId || 'unassigned'] || 1);
      return { minHeight: (68 + (laneCount - 1) * 32) + 'px' };
    },
    taskBarStyle(task) {
      const start = new Date(task.scheduledStart);
      const dayStart = new Date(this.currentDate); dayStart.setHours(0,0,0,0);
      const startMins = (start - dayStart) / 60000;
      const left = startMins * (this.hourWidth / 60);

      let width = this.hourWidth;
      if (task.scheduledEnd) {
        const end = new Date(task.scheduledEnd);
        const durationMins = (end - start) / 60000;
        width = Math.max(durationMins * (this.hourWidth / 60), 20);
      }
      // Tasks sit below however many assignment lanes the row has
      const laneCount = Math.max(1, this.dayLanes.counts[task.assignedToUserId || 'unassigned'] || 1);
      return { left: left + 'px', width: width + 'px', background: '#6c757d', top: (36 + (laneCount - 1) * 32) + 'px', height: '22px' };
    },
    openCreateAssignment(userId, event) {
      if (!this.isAdmin) return;

      let startTime = '';
      if (this.currentView === 'day' && event) {
        const rect = event.currentTarget?.getBoundingClientRect?.();
        if (rect) {
          const x = event.clientX - rect.left;
          const rawMins = Math.floor(x / (this.hourWidth / 60));
          const mins = Math.round(rawMins / 15) * 15;
          const d = new Date(this.currentDate);
          d.setHours(0, mins, 0, 0);
          startTime = toLocalDatetimeInput(d);
        }
      }

      this.projectSearch = '';
      this.editingAssignment = null;
      this.assignmentForm = {
        projectId: null,
        userId: null,
        userIds: userId ? [userId] : [],
        typeIds: [],
        startTime,
        endTime: '',
        comment: '',
        multiplier: null,
      };
      this.showAssignmentModal = true;
    },
    onMonthCellClick(day) {
      if (!day || !this.monthCreateEnabled) return;
      const d = new Date(day.date);
      d.setHours(8, 0, 0, 0);
      this.projectSearch = '';
      this.editingAssignment = null;
      this.assignmentForm = {
        projectId: null,
        userId: null,
        userIds: [],
        typeIds: [],
        startTime: toLocalDatetimeInput(d),
        endTime: '',
        comment: '',
        multiplier: null,
      };
      this.showAssignmentModal = true;
    },
    onAssignmentChipClick(a, event) {
      if (!this.plannerConfigs.enableMonthViewCreation) return;
      this.showBarDetail('assignment', a, event);
    },
    openEditAssignment(assignment) {
      if (!this.isAdmin) return;
      this.editingAssignment = assignment;
      this.projectSearch = '';
      this.assignmentForm = {
        projectId: assignment.projectId && (assignment.projectId.value || assignment.projectId),
        userId: assignment.userId || null,
        userIds: [],
        typeIds: [],
        startTime: toLocalDatetimeInput(assignment.startTime),
        endTime: assignment.endTime ? toLocalDatetimeInput(assignment.endTime) : '',
        comment: assignment.comment || '',
        multiplier: assignment.multiplier ?? null,
      };
      this.detailPopup = null;
      this.showAssignmentModal = true;
    },
    closeAssignmentModal() {
      this.showAssignmentModal = false;
      this.projectSearch = '';
      this.editingAssignment = null;
    },
    async deleteAssignment(assignment) {
      // Close the popup before the dialog so the click-away listener can't swallow dialog clicks
      this.detailPopup = null;
      const result = await swal.fire({
        title: this.$t('delete.areYouSure'),
        text: this.$t('delete.noRevert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('delete.yesDeleteIt'),
        cancelButtonText: this.$t('delete.cancel'),
        customClass: { confirmButton: 'btn btn-success btn-fill', cancelButton: 'btn btn-danger btn-fill' },
        buttonsStyling: false,
      });
      if (!result.isConfirmed) return;
      this.deletingAssignment = true;
      try {
        await mainApi.delete(`/Project/Assignment/${assignment.id}`);
        this.$toast.success(this.$t('common.deleted'));
        this.detailPopup = null;
        await this.fetchProjectAssignments();
      } catch (e) {
        console.error('Failed to delete assignment:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToDelete'), { timeout: 0 });
      } finally {
        this.deletingAssignment = false;
      }
    },
    openCreateReservation(tool, event) {
      if (!this.isAdmin || !this.enableFutureReservation) return;

      let startTime = '';
      let endTime = '';
      if (this.currentView === 'day' && event && event.currentTarget && event.currentTarget.getBoundingClientRect) {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const rawMins = Math.floor(x / (this.hourWidth / 60));
        const mins = Math.round(rawMins / 15) * 15;
        const start = new Date(this.currentDate);
        start.setHours(0, mins, 0, 0);
        const end = new Date(start);
        end.setHours(end.getHours() + 1);
        startTime = toLocalDatetimeInput(start);
        endTime = toLocalDatetimeInput(end);
      }

      this.reservationProjectSearch = '';
      this.editingReservation = null;
      this.reservationForm = {
        projectId: null,
        startTime,
        endTime,
        comment: '',
        email: '',
        quantity: null,
      };
      // If a tool row was clicked, prefill it as the selected tool
      this.selectedReservationTool = tool ? { id: tool.id, name: tool.name } : null;
      this.fetchAvailableToolTypes();
      this.showReservationModal = true;
    },
    openEditReservation(reservation) {
      if (!this.isAdmin || !this.enableFutureReservation) return;
      this.editingReservation = reservation;
      this.reservationProjectSearch = '';
      const projectId = reservation.projectId && (reservation.projectId.value || reservation.projectId);
      this.reservationForm = {
        projectId: projectId || null,
        startTime: toLocalDatetimeInput(reservation.startTime),
        endTime: reservation.endTime ? toLocalDatetimeInput(reservation.endTime) : '',
        comment: reservation.comment || '',
        email: reservation.externalUserEmail || '',
        quantity: reservation.quantity ?? null,
      };
      this.selectedReservationTool = reservation.tool
        ? {
            id: reservation.toolId && (reservation.toolId.value || reservation.toolId),
            name: reservation.tool.name,
            description: reservation.tool.description,
            multiTool: reservation.tool.multiTool,
          }
        : null;
      this.detailPopup = null;
      this.fetchAvailableToolTypes();
      this.showReservationModal = true;
    },
    closeReservationModal() {
      this.showReservationModal = false;
      this.reservationProjectSearch = '';
      this.selectedReservationTool = null;
      this.editingReservation = null;
    },
    onReservationTimeChanged() {
      // Times changed → previously-picked tool may no longer be available
      this.selectedReservationTool = null;
    },
    syncEndDateToStart(startStr, endStr) {
      if (!startStr) return endStr;
      const startDatePart = startStr.slice(0, 10);
      if (!endStr) return startStr;
      return `${startDatePart}T${endStr.slice(11)}`;
    },
    onAssignmentStartChanged() {
      this.assignmentForm.endTime = this.syncEndDateToStart(
        this.assignmentForm.startTime,
        this.assignmentForm.endTime,
      );
    },
    onReservationStartChanged() {
      this.reservationForm.endTime = this.syncEndDateToStart(
        this.reservationForm.startTime,
        this.reservationForm.endTime,
      );
      this.selectedReservationTool = null;
    },
    onTaskStartChanged() {
      this.form.scheduledEnd = this.syncEndDateToStart(
        this.form.scheduledStart,
        this.form.scheduledEnd,
      );
    },
    onWeekToolCellClick(tool, date) {
      if (!this.isAdmin || !this.enableFutureReservation) return;
      const start = new Date(date); start.setHours(8, 0, 0, 0);
      const end = new Date(date); end.setHours(9, 0, 0, 0);
      this.reservationProjectSearch = '';
      this.editingReservation = null;
      this.reservationForm = {
        projectId: null,
        startTime: toLocalDatetimeInput(start),
        endTime: toLocalDatetimeInput(end),
        comment: '',
        email: '',
        quantity: null,
      };
      this.selectedReservationTool = { id: tool.id, name: tool.name };
      this.fetchAvailableToolTypes();
      this.showReservationModal = true;
    },
    openToolPickModal() {
      if (!this.reservationFormHasValidTimes) return;
      this.pendingPickedTool = this.selectedReservationTool ? { ...this.selectedReservationTool } : null;
      this.availableToolsSearch = '';
      this.availableToolTypeFilter = '';
      this.availableToolsPagination = { ...this.availableToolsPagination, currentPage: 1, total: 0 };
      this.showToolPickModal = true;
      this.fetchAvailableTools(1);
    },
    closeToolPickModal() {
      this.showToolPickModal = false;
      this.pendingPickedTool = null;
    },
    confirmToolPick() {
      if (!this.pendingPickedTool) return;
      this.selectedReservationTool = this.pendingPickedTool;
      if (this.selectedReservationTool.multiTool && this.reservationForm.quantity == null) {
        this.reservationForm.quantity = 1;
      } else if (!this.selectedReservationTool.multiTool) {
        this.reservationForm.quantity = null;
      }
      this.closeToolPickModal();
    },
    onAvailableToolsSearchInput() {
      if (this.availableToolsSearchTimer) clearTimeout(this.availableToolsSearchTimer);
      this.availableToolsSearchTimer = setTimeout(() => this.fetchAvailableTools(1), 300);
    },
    async fetchAvailableTools(page) {
      if (!this.reservationFormHasValidTimes) return;
      this.availableToolsLoading = true;
      try {
        const startIso = fromLocalDatetimeInput(this.reservationForm.startTime);
        const endIso = fromLocalDatetimeInput(this.reservationForm.endTime);
        let url = `/Tool?currentPage=${page}&pageSize=${this.availableToolsPagination.perPage}&projectStatusId=2`
          + `&noProjectAssignmentBefore=${encodeURIComponent(startIso)}`
          + `&noProjectAssignmentAfter=${encodeURIComponent(endIso)}`;
        if (this.availableToolsSearch) url += `&searchString=${encodeURIComponent(this.availableToolsSearch)}`;
        if (this.availableToolTypeFilter) url += `&toolTypeId=${this.availableToolTypeFilter}`;

        const res = await mainApi.get(url);
        this.availableToolsPagination = {
          ...this.availableToolsPagination,
          currentPage: page,
          total: res.data.totalItems || 0,
        };
        this.availableTools = (res.data.result || []).map(t => ({
          id: t.id,
          name: t.name,
          description: t.description,
          multiTool: t.multiTool,
          image: t.toolImageThumbnailBytes ? 'data:image/jpeg;base64,' + t.toolImageThumbnailBytes : null,
        }));
      } catch (e) {
        console.error('Failed to load available tools:', e); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      } finally {
        this.availableToolsLoading = false;
      }
    },
    isValidEmail(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); },
    async saveReservation() {
      if (!this.selectedReservationTool) return;
      if (this.reservationForm.email && !this.isValidEmail(this.reservationForm.email)) {
        this.$toast.error(this.$t('errors.invalidEmail'), { timeout: 2500 });
        return;
      }
      this.savingReservation = true;
      try {
        const quantity = this.reservationForm.quantity != null && this.reservationForm.quantity !== ''
          ? Number(this.reservationForm.quantity)
          : null;
        if (this.editingReservation) {
          await mainApi.put(`/Project/Assignment/${this.editingReservation.id}`, {
            projectId: this.reservationForm.projectId,
            userId: this.editingReservation.userId || null,
            toolId: this.selectedReservationTool.id,
            startTime: fromLocalDatetimeInput(this.reservationForm.startTime),
            endTime: fromLocalDatetimeInput(this.reservationForm.endTime),
            comment: this.reservationForm.comment || null,
            externalUserEmail: this.reservationForm.email || null,
            active: false,
            quantity,
          });
        } else {
          await mainApi.post('/Project/Assignment?validateProjectDates=false', {
            projectId: this.reservationForm.projectId,
            toolId: this.selectedReservationTool.id,
            startTime: fromLocalDatetimeInput(this.reservationForm.startTime),
            endTime: fromLocalDatetimeInput(this.reservationForm.endTime),
            comment: this.reservationForm.comment || null,
            externalUserEmail: this.reservationForm.email || null,
            preCreated: true,
            active: false,
            quantity,
          });
        }
        this.$toast.success(this.$t('common.saved'));
        this.closeReservationModal();
        await this.fetchProjectAssignments();
      } catch (e) {
        console.error('Failed to save reservation:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.savingReservation = false;
      }
    },
    async confirmReservation(reservation) {
      this.confirmingReservation = true;
      try {
        const toolId = reservation.toolId && (reservation.toolId.value || reservation.toolId);
        const projectId = reservation.projectId && (reservation.projectId.value || reservation.projectId);

        await mainApi.post('/Tool/Reserve/External', {
          toolId,
          externalUser: reservation.comment,
          externalUserEmail: reservation.externalUserEmail,
          rented: true,
          projectId,
          maxReservationDays: null,
          dailyPrice: null,
          quantity: reservation.quantity,
        });

        await mainApi.put('/Project/Assignment/' + reservation.id, {
          projectId,
          userId: reservation.userId || null,
          toolId,
          startTime: reservation.startTime,
          endTime: reservation.endTime,
          externalUserEmail: reservation.externalUserEmail,
          comment: reservation.comment,
          active: true,
        });

        this.$toast.success(this.$t('projectAssignments.confirmed'), { timeout: 1500 });
        this.detailPopup = null;
        await this.fetchProjectAssignments();
      } catch (e) {
        console.error('Failed to confirm reservation:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.confirmingReservation = false;
      }
    },
    async deleteReservation(reservation) {
      this.detailPopup = null;
      if (!confirm(this.$t('delete.areYouSure'))) return;
      try {
        await mainApi.delete(`/Project/Assignment/${reservation.id}`);
        this.$toast.success(this.$t('common.deleted'));
        this.detailPopup = null;
        await this.fetchProjectAssignments();
      } catch (e) {
        console.error('Failed to delete reservation:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToDelete'), { timeout: 0 });
      }
    },
    filteredProjectsForItem(item) {
      if (!item.projectSearch) return this.projects;
      const q = item.projectSearch.toLowerCase();
      return this.projects.filter(p => (p.name || p.key || '').toLowerCase().includes(q));
    },
    parsedMultiplier() {
      if (!this.plannerConfigs.enableMultiplier) return undefined;
      const v = this.assignmentForm.multiplier;
      return v != null && v !== '' ? Number(v) : null;
    },
    async saveAssignment() {
      this.savingAssignment = true;
      try {
        if (this.editingAssignment) {
          const a = this.editingAssignment;
          const multiplier = this.parsedMultiplier();
          await mainApi.put(`/Project/Assignment/${a.id}`, {
            ...a,
            projectId: this.assignmentForm.projectId,
            userId: this.assignmentForm.userId || null,
            startTime: fromLocalDatetimeInput(this.assignmentForm.startTime),
            endTime: fromLocalDatetimeInput(this.assignmentForm.endTime) || null,
            comment: this.assignmentForm.comment || null,
            ...(multiplier !== undefined ? { multiplier } : {}),
          });
        } else {
          const multiplier = this.parsedMultiplier();
          const basePayload = {
            projectId: this.assignmentForm.projectId,
            startTime: fromLocalDatetimeInput(this.assignmentForm.startTime),
            // With status colors, EndTime means "actually finished at" — new assignments start without one
            endTime: this.plannerConfigs.useStatusColors ? null : (fromLocalDatetimeInput(this.assignmentForm.endTime) || null),
            comment: this.assignmentForm.comment || null,
            preCreated: true,
            ...(multiplier !== undefined ? { multiplier } : {}),
          };
          // One assignment per selected user (empty selection = one unassigned assignment),
          // times one per checked assignment type when types are enabled.
          const targetUserIds = this.assignmentForm.userIds.length ? this.assignmentForm.userIds : [null];
          const typeIds = this.plannerConfigs.enableAssignmentTypes ? this.assignmentForm.typeIds : [null];
          const combos = targetUserIds.flatMap(uid => typeIds.map(tid => ({ uid, tid })));
          const results = await Promise.allSettled(
            combos.map(({ uid, tid }) => mainApi.post('/Project/Assignment', {
              ...basePayload,
              userId: uid || null,
              // Directly assigned = in progress (same state "assign to me" produces); unassigned = pending
              active: !!uid,
              ...(tid ? { projectAssignmentTypeId: tid } : {}),
            }))
          );
          const failed = results.filter(r => r.status === 'rejected');
          if (failed.length) {
            const firstError = extractApiError(failed[0].reason);
            await this.fetchProjectAssignments();
            this.$toast.error(
              this.$t('golf.someAssignmentsFailed', { failed: failed.length, total: results.length })
                + (firstError ? ` — ${firstError}` : ''),
              { timeout: 0 }
            );
            return;
          }
        }
        this.$toast.success(this.$t('common.saved'));
        this.closeAssignmentModal();
        await this.fetchProjectAssignments();
      } catch (e) {
        console.error('Failed to save assignment:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.savingAssignment = false;
      }
    },
    openCreate(userId, event) {
      if (!this.isAdmin) return;
      this.editingTask = null;

      let scheduledStart = '';
      if (this.currentView === 'day' && event) {
        const rect = event.currentTarget?.getBoundingClientRect?.();
        if (rect) {
          const x = event.clientX - rect.left;
          const rawMins = Math.floor(x / (this.hourWidth / 60));
          const mins = Math.round(rawMins / 15) * 15;
          const d = new Date(this.currentDate);
          d.setHours(0, mins, 0, 0);
          scheduledStart = toLocalDatetimeInput(d);
        }
      }

      this.form = {
        taskId: null,
        assignedToUserId: userId,
        scheduledStart,
        scheduledEnd: '',
      };
      this.showModal = true;
    },
    openEdit(task) {
      this.editingTask = task;
      this.form = {
        taskId: task.id,
        assignedToUserId: task.assignedToUserId || null,
        scheduledStart: toLocalDatetimeInput(task.scheduledStart),
        scheduledEnd: toLocalDatetimeInput(task.scheduledEnd),
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingTask = null;
    },
    async saveTask() {
      if (!this.form.taskId) return;
      this.saving = true;
      try {
        const payload = {
          assignedToUserId: this.form.assignedToUserId || null,
          scheduledStart: fromLocalDatetimeInput(this.form.scheduledStart),
          scheduledEnd: fromLocalDatetimeInput(this.form.scheduledEnd),
        };

        await golfApi.put(`/tasks/${this.form.taskId}`, payload);

        this.$toast.success(this.$t('common.saved'));
        this.closeModal();
        await this.fetchTasks();
      } catch (e) {
        console.error('Failed to save task:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.saving = false;
      }
    },
    onDocumentClick(e) {
      // Capture-phase click-away for the detail popup: fires before target handlers, so it
      // also works for clicks that stop propagation or land outside the planner (sidebar,
      // navbar). The dismissing click is swallowed so it doesn't also trigger the click
      // target underneath (e.g. month-cell create).
      if (!this.detailPopup) return;
      const pop = this.$refs.detailPopupEl;
      if (pop && pop.contains(e.target)) return;
      this.detailPopup = null;
      e.stopPropagation();
      e.preventDefault();
    },
    showBarDetail(type, data, event) {
      const popupWidth = 280;
      const popupHeightEstimate = 220;
      const target = event && event.currentTarget;
      const rect = target && target.getBoundingClientRect ? target.getBoundingClientRect() : null;

      let x, y;
      if (rect) {
        x = rect.left;
        y = rect.bottom + 6;
        if (x + popupWidth > window.innerWidth) x = Math.max(8, window.innerWidth - popupWidth - 8);
        if (y + popupHeightEstimate > window.innerHeight) {
          const above = rect.top - popupHeightEstimate - 6;
          y = above >= 8 ? above : Math.max(8, window.innerHeight - popupHeightEstimate - 8);
        }
      } else {
        x = (event?.clientX ?? 0) + 12;
        y = (event?.clientY ?? 0) + 12;
      }
      this.detailPopup = { type, data, x, y };
    },
    formatBarTime(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    },
    onDragStart(task, event) {
      this.dragTask = task;
      this.dragAssignment = null;
      this.dragGrabOffsetPx = event?.offsetX ?? 0;
    },
    onDragStartAssignment(assignment, event) {
      this.dragAssignment = assignment;
      this.dragTask = null;
      this.dragGrabOffsetPx = event?.offsetX ?? 0;
    },
    computeDroppedTimes(originalStart, originalEnd, event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const rawX = event.clientX - rect.left - this.dragGrabOffsetPx;
      const minsPerPx = 60 / this.hourWidth;
      let startMins = Math.round((rawX * minsPerPx) / 15) * 15;

      const dayEndMins = 24 * 60;
      let durationMins = 60;
      if (originalStart && originalEnd) {
        durationMins = Math.max(15, Math.round((new Date(originalEnd) - new Date(originalStart)) / 60000));
      }
      if (durationMins > dayEndMins) durationMins = dayEndMins;

      if (startMins < 0) startMins = 0;
      if (startMins + durationMins > dayEndMins) startMins = dayEndMins - durationMins;

      const dayStart = new Date(this.currentDate); dayStart.setHours(0, 0, 0, 0);
      const newStart = new Date(dayStart.getTime() + startMins * 60000);
      const newEnd = new Date(dayStart.getTime() + (startMins + durationMins) * 60000);
      return { newStart: newStart.toISOString(), newEnd: newEnd.toISOString() };
    },
    async onDrop(userId, event) {
      this.dragOverUser = undefined;
      const isDayView = this.currentView === 'day';

      if (this.dragTask) {
        const task = this.dragTask;
        let scheduledStart = task.scheduledStart || null;
        let scheduledEnd = task.scheduledEnd || null;

        if (isDayView && event && event.currentTarget) {
          const times = this.computeDroppedTimes(task.scheduledStart, task.scheduledEnd, event);
          scheduledStart = times.newStart;
          scheduledEnd = times.newEnd;
        }

        const userUnchanged = task.assignedToUserId === userId;
        const timesUnchanged = scheduledStart === task.scheduledStart && scheduledEnd === task.scheduledEnd;
        if (userUnchanged && timesUnchanged) { this.dragTask = null; return; }

        try {
          await golfApi.put(`/tasks/${task.id}`, {
            title: task.title,
            description: task.description || null,
            assignedToUserId: userId,
            scheduledStart,
            scheduledEnd,
          });
          this.$toast.success(this.$t('common.saved'));
          await this.fetchTasks();
        } catch (e) {
          console.error('Failed to reassign task:', e); // eslint-disable-line no-console
          this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
        } finally {
          this.dragTask = null;
        }
      } else if (this.dragAssignment) {
        const a = this.dragAssignment;
        let startTime = a.startTime;
        let endTime = a.endTime;

        if (isDayView && event && event.currentTarget) {
          const times = this.computeDroppedTimes(a.startTime, a.endTime, event);
          startTime = times.newStart;
          endTime = times.newEnd;
          // With status colors, EndTime means "actually finished at" — dragging a
          // pending assignment must not stamp one and flip it to finished
          if (this.plannerConfigs.useStatusColors && !a.endTime) endTime = null;
        }

        const userUnchanged = a.userId === userId;
        const timesUnchanged = startTime === a.startTime && endTime === a.endTime;
        if (userUnchanged && timesUnchanged) { this.dragAssignment = null; return; }

        try {
          await mainApi.put(`/Project/Assignment/${a.id}`, {
            ...a,
            userId,
            startTime,
            endTime,
            projectId: a.projectId && (a.projectId.value || a.projectId),
          });
          this.$toast.success(this.$t('common.saved'));
          await this.fetchProjectAssignments();
        } catch (e) {
          console.error('Failed to reassign assignment:', e); // eslint-disable-line no-console
          this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
        } finally {
          this.dragAssignment = null;
        }
      }
    },
    onDragLeave(userId) {
      if (this.dragOverUser === userId) this.dragOverUser = undefined;
    },

    // ── Routines ──
    async fetchRoutines() {
      try {
        const res = await golfApi.get('/routines');
        this.routines = res.data;
      } catch (e) {
        console.error('Failed to load routines:', e); // eslint-disable-line no-console
      }
    },
    openRoutineBuilder() {
      this.routineForm = { name: '', items: [] };
      this.showRoutineModal = true;
    },
    closeRoutineModal() {
      this.showRoutineModal = false;
    },
    addRoutineItem() {
      this.routineForm.items.push({
        projectId: null,
        assignedToUserId: null,
        startTime: '07:00',
        endTime: '08:00',
        projectSearch: '',
      });
    },
    removeRoutineItem(idx) {
      this.routineForm.items.splice(idx, 1);
    },
    async saveRoutine() {
      this.savingRoutine = true;
      try {
        const payload = {
          name: this.routineForm.name,
          items: this.routineForm.items.map((item, idx) => ({
            projectId: item.projectId,
            assignedToUserId: item.assignedToUserId || null,
            startTime: item.startTime || '07:00',
            endTime: item.endTime || null,
            sortOrder: idx,
          })),
        };
        const res = await golfApi.post('/routines', payload);
        this.routines.push(res.data);
        this.selectedRoutineId = res.data.id;
        this.$toast.success(this.$t('common.saved'));
        this.closeRoutineModal();
      } catch (e) {
        console.error('Failed to save routine:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.savingRoutine = false;
      }
    },
    async deleteRoutine() {
      if (!this.selectedRoutineId) return;
      const result = await swal.fire({
        title: this.$t('delete.areYouSure'),
        text: this.$t('golf.deleteRoutineWarning'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('delete.yesDeleteIt'),
        cancelButtonText: this.$t('delete.cancel'),
        customClass: { confirmButton: 'btn btn-success btn-fill', cancelButton: 'btn btn-danger btn-fill' },
        buttonsStyling: false,
      });
      if (!result.isConfirmed) return;
      try {
        await golfApi.delete(`/routines/${this.selectedRoutineId}`);
        this.routines = this.routines.filter(r => r.id !== this.selectedRoutineId);
        this.selectedRoutineId = null;
        this.$toast.success(this.$t('common.deleted'));
      } catch (e) {
        console.error('Failed to delete routine:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async applyRoutine() {
      if (!this.selectedRoutineId) return;
      this.applyingRoutine = true;
      try {
        const date = toLocalDateString(this.currentDate);
        await golfApi.post(`/routines/${this.selectedRoutineId}/apply`, { date });
        this.$toast.success(this.$t('golf.routineApplied'));
        // Routines now create ProjectAssignments — refetch assignments
        await this.fetchProjectAssignments();
      } catch (e) {
        console.error('Failed to apply routine:', e); // eslint-disable-line no-console
        this.$toast.error(extractApiError(e) || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.applyingRoutine = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Gantt ── */
.gantt-wrapper {
  overflow-x: auto;
}
.gantt-container {
  min-width: max-content;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
}
.gantt-header {
  display: flex;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  position: sticky;
  top: 0;
  z-index: 2;
}
.gantt-label-col {
  width: 150px;
  min-width: 150px;
  padding: 6px 10px;
  border-right: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 13px;
  background: #f8f9fa;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.gantt-time-area {
  display: flex;
}
.gantt-hour-cell {
  text-align: center;
  font-size: 11px;
  color: #888;
  padding: 4px 0;
  border-right: 1px solid #e9ecef;
  flex-shrink: 0;
}
.gantt-row {
  display: flex;
  border-bottom: 1px solid #e9ecef;
  min-height: 68px;
  transition: background 0.15s;
}
.gantt-row.drag-over .gantt-bar-area {
  background: rgba(123, 47, 247, 0.07);
}
.gantt-bar-area {
  position: relative;
  flex: 0 0 auto;
  min-height: 68px;
  cursor: crosshair;
}
.gantt-grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #f0f0f0;
  pointer-events: none;
}
.gantt-bar {
  position: absolute;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  cursor: pointer;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0,0,0,.2);
  transition: filter 0.15s;
  z-index: 1;
}
.gantt-bar:hover { filter: brightness(1.1); }
/* Assignment bars - primary lane, solid colors */
.gantt-bar-assignment {
  opacity: 0.92;
}
/* Task bars - secondary lane, muted */
.gantt-bar-task {
  opacity: 0.85;
  font-size: 11px;
}
.gantt-bar-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gantt-user-name {
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.planner-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover; /* handles non-square images without distortion */
  flex-shrink: 0;
  margin-right: 8px;
  vertical-align: middle;
}
.planner-avatar-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #7b2ff7;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

/* ── Legend ── */
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
}
.legend-assignment { background: #2196F3; }
.legend-task { background: #6c757d; }
.legend-status-pending { background: #F44336; }
.legend-status-ongoing { background: #4CAF50; }
.legend-status-finished { background: #2196F3; }
.legend-reservation-active { background: #198754; }
.legend-reservation-pending { background: #fd7e14; }

/* Equipment-axis reservation bars */
.gantt-bar-reservation-active { background: #198754; opacity: 0.92; }
.gantt-bar-reservation-pending { background: #fd7e14; opacity: 0.92; }

.week-reservation-active-chip { background: #198754; cursor: pointer; }
.week-reservation-pending-chip { background: #fd7e14; cursor: pointer; }
.month-reservation-active-chip { background: #198754; }
.month-reservation-pending-chip { background: #fd7e14; }

/* Tool pick sub-modal */
.tool-pick-list {
  max-height: 380px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.tool-pick-row {
  cursor: pointer;
  transition: background 0.12s;
}
.tool-pick-row:hover {
  background: #f8f9fa;
}
.tool-pick-row-selected {
  background: #e8f4ea !important;
}

/* ── Week ── */
.week-table th, .week-table td { vertical-align: top; font-size: 13px; }
.week-label-col { width: 120px; min-width: 100px; }
.week-cell { min-height: 60px; padding: 4px; }
.week-task-chip {
  background: #6c757d;
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  margin-bottom: 2px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.week-task-chip:hover { filter: brightness(1.1); }
.week-assignment-chip {
  background: #2196F3;
  cursor: default;
}
.chip-clickable { cursor: pointer; }
.bg-purple-light { background: rgba(33, 107, 46, 0.08) !important; }

/* ── Month ── */
.month-table th { font-size: 12px; text-transform: uppercase; color: #888; }
.month-cell { width: 14.28%; min-height: 80px; vertical-align: top; padding: 4px; font-size: 13px; }
.month-day-number { font-weight: 600; margin-bottom: 2px; }
.month-task-chip {
  background: #6c757d;
  color: #fff;
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 11px;
  margin-bottom: 2px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.month-task-chip:hover { filter: brightness(1.1); }
.month-assignment-chip {
  background: #2196F3;
  cursor: default;
}
.month-cell-clickable { cursor: pointer; }
.today-cell { background: rgba(33, 107, 46, 0.08) !important; }

/* ── Modal ── */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-dialog-custom {
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
}

/* golf-green accent button (class kept as .btn-purple to avoid template churn) */
.btn-purple { background: #216b2e; color: #fff; border-color: #216b2e; }
.btn-purple:hover { background: #185221; border-color: #185221; color: #fff; }

/* ── Routines ── */
.routines-bar {
  background: #f0f9f1;
  border: 1px solid #cfe9d4;
}
.routine-item-row {
  background: #fafafa;
}
.routine-item-row:hover {
  background: #e8f4ea;
}

/* ── Bar detail popup ── */
.bar-detail-popup {
  position: fixed;
  z-index: 1100;
  min-width: 220px;
  max-width: 300px;
  border-radius: 8px;
  font-size: 13px;
  pointer-events: auto;
}

/* ── Dark mode ── */
body.dark-version .gantt-container { background: #202940; border-color: rgba(255,255,255,.1); }
body.dark-version .gantt-header { background: #1a2035; border-bottom-color: rgba(255,255,255,.1); }
body.dark-version .gantt-label-col { background: #1a2035; border-right-color: rgba(255,255,255,.08); color: rgba(255,255,255,.85); }
body.dark-version .gantt-hour-cell { color: rgba(255,255,255,.5); border-right-color: rgba(255,255,255,.06); }
body.dark-version .gantt-row { border-bottom-color: rgba(255,255,255,.06); }
body.dark-version .gantt-grid-line { background: rgba(255,255,255,.05); }
body.dark-version .tool-pick-list { border-color: rgba(255,255,255,.1); }
body.dark-version .tool-pick-row:hover { background: rgba(255,255,255,.05); }
body.dark-version .tool-pick-row-selected { background: rgba(33,107,46,.25) !important; }
body.dark-version .routines-bar { background: rgba(33,107,46,.15); border-color: rgba(33,107,46,.35); }
body.dark-version .routine-item-row { background: #1a2035; }
body.dark-version .routine-item-row:hover { background: #2a3454; }
body.dark-version .month-table th { color: rgba(255,255,255,.5); }
</style>
